<script setup lang="ts">
import type { Gap } from "@kaleidoscope-solutions/engine-wasm"
import { setCellClassByCell } from "@/utils/table"
import { useVirtualList } from "@vueuse/core"
import { computed, toRef } from "vue"

export type CellData = string | CellData[]
export type TableData = CellData[][]

interface Props {
  tableData: TableData
  readOnly?: boolean
  nested?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  readOnly: true
})

const tableData = toRef(props, "tableData")

const columnsCount = computed(() => tableData.value[0]?.length || 0)
const rowsCount = computed(() => tableData.value.length || 0)

const rowHeight = 34 // 32 + 2 (border)

function calculateRowHeight(rowData: CellData[]) {
  return rowHeight * rowData.reduce((acc, cellData) => {
    if (Array.isArray(cellData)) {
      const nestedHeight = calculateRowHeight(cellData)
      if (nestedHeight > acc) {
        acc = nestedHeight
      }
    }

    return acc
  }, 1)
}

const rowHeights = computed(() => tableData.value.map((rowData) => calculateRowHeight(rowData)))

const { list, containerProps, wrapperProps } = useVirtualList(tableData, {
  itemHeight: (row) => rowHeights.value[row],
  overscan: 1000
})

function getCellClasses(rowIndex: number, cellIndex: number) {
  const hierarchyLevel: number = 0
  const nextRowNested: boolean = false
  const firstRow: boolean = rowIndex === 0
  const lastRow: boolean = rowIndex === rowsCount.value - 1
  const firstCellInRow: boolean = cellIndex === 0
  const lastCellInRow: boolean = cellIndex === columnsCount.value - 1
  const adjacentCellGaps: Gap[] = []

  return setCellClassByCell({
    hierarchyLevel,
    nextRowNested,
    firstRow,
    lastRow,
    firstCellInRow,
    lastCellInRow,
    adjacentCellGaps
  })
}
</script>

<template>
  <div
    v-if="tableData.length"
    v-bind="containerProps"
  >
    <div
      v-bind="wrapperProps"
      class="inline-grid text-sm"
      :style="{ gridTemplateColumns: `repeat(${columnsCount}, max-content)`, height: '100%' }"
    >
      <template
        v-for="(row, rowIndex) in list"
        :key="row.index"
      >
        <div
          v-for="(cell, cellIndex) in row.data"
          :key="cellIndex"
          :class="[{
            'cell--readOnly': readOnly,
            'cell--ColumnHeader': rowIndex === 0,
            'cell--Cell': rowIndex > 0,
          }, getCellClasses(row.index, cellIndex)]"
          class="group/cell"
        >
          <div class="cell-inner">
            <slot :cell="cell">
              <template v-if="Array.isArray(cell)">
                <ArrayTable
                  :nested="true"
                  :table-data="(cell as TableData)"
                  :read-only="false"
                />
              </template>
              <template v-else>
                {{ cell }}
              </template>
            </slot>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
